import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import urlJoin from 'url-join';
import {SearchPlacesData} from '../../types/SearchPlacesData';
import {SearchResult} from '../../types/SearchResult';
import {SearchResultsPayload} from '../../types/SearchResultsPayload';
import {SearchState} from '../../types/SearchState';
import {SearchSuggestItem} from '../../types/SearchSuggestItem';
import {SearchSuggestPayload} from '../../types/SearchSuggestPayload';
import {TimeslotsResponse} from '../../types/SearchTimeslot';
import {ApiHelper} from '../api.helper';
import {AppConfigService} from '../app-config.service';
import {SearchHelperService} from '../search-helper.service';
import {ToolsService} from '../tools.service';
import {WebsiteContent} from '../../types/WebsiteContent';
import {UrlTermsSymbolsEnum} from '../../enums/SeoEnums';

enum ContentTypeEnum {
  CARE_PROVIDER_UNIT_TYPE = 'careprovider-types-content',
  CARE_SERVICE_TYPE = 'careservice-types-content',
  SYMPTOM = 'symptoms-content',
}

@Injectable({
  providedIn: 'root',
})
export class SearchApiService {
  constructor(
    private http: HttpClient,
    private searchHelper: SearchHelperService,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private apiHelper: ApiHelper,
  ) {}

  public getSearchResults(payload: SearchResultsPayload, state: SearchState): Observable<SearchResult> {
    let url: string = this.appConfigService.appConfig.apiUrl + 'search/providers';
    let params = this.tools.buildHttpParams(payload);

    if (this.searchHelper.shouldDisplaySymptomPage(state)) {
      url = this.appConfigService.appConfig.apiUrl + `landingpage/symptom/${state.symptomUrlPath}`;
      params = null;
    } else if (this.searchHelper.shouldDisplayServiceTypePage(state)) {
      url = this.appConfigService.appConfig.apiUrl + `landingpage/careservicetype/${state.careServiceTypeUrlPath}`;
      params = null;
    } else if (this.searchHelper.shouldUseServiceTypeSearchEndpoint(state)) {
      url = this.appConfigService.appConfig.apiUrl + 'search/treatments';
    }

    return this.http
      .get<SearchResult>(url, {
        params,
      })
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public getPlacesResults(payload: SearchResultsPayload): Observable<SearchPlacesData> {
    const params = this.tools.buildHttpParams(payload);
    let url: string = this.appConfigService.appConfig.apiUrl + 'search/places';
    return this.http
      .get<SearchPlacesData>(url, {
        params,
      })
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public freeSearchSuggest(suggestPayload: SearchSuggestPayload): Observable<SearchSuggestItem[]> {
    const params = this.tools.buildHttpParams(suggestPayload);

    return this.http
      .get<SearchSuggestItem[]>(this.appConfigService.appConfig.apiUrl + 'suggest', {
        params: params,
      })
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public getTimeslots(careProviderUnitIds: number[]): Observable<TimeslotsResponse> {
    return this.http
      .get<TimeslotsResponse>(urlJoin(this.appConfigService.appConfig.apiUrl, '/caregiverunit/timeslots'), {
        params: {
          id: careProviderUnitIds,
          t: new Date().getTime().toString(),
        },
      })
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public getWebsiteContent(state: SearchState): Observable<WebsiteContent> {
    let contentType: ContentTypeEnum;
    let fileName: string;
    if (state.caregiverUnitTypeUrlPath) {
      contentType = ContentTypeEnum.CARE_PROVIDER_UNIT_TYPE;
      fileName = `${state.caregiverUnitTypeUrlPath}-${UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE}`;
    }
    if (state.careServiceTypeUrlPath) {
      contentType = ContentTypeEnum.CARE_SERVICE_TYPE;
      fileName = `${state.careServiceTypeUrlPath}-${UrlTermsSymbolsEnum.SERVICE_TYPE}`;
    }
    if (state.symptomUrlPath) {
      contentType = ContentTypeEnum.SYMPTOM;
      fileName = `${state.symptomUrlPath}-${UrlTermsSymbolsEnum.SYMPTOM}`;
    }
    const url: string = this.appConfigService.appConfig.amazonS3ContentUrl + contentType + '/' + fileName;
    return this.http.get<WebsiteContent>(url).pipe(catchError((error) => this.apiHelper.handleError(error, true)));
  }
}
